// Translation lookup. ICU MessageFormat, because the copy in /docs/design
// already uses plurals ("{count, plural, one {# selected} other {# selected}}")
// and concatenating translated fragments is forbidden by CLAUDE.md — in a
// language with different plural rules the pieces stop fitting together.
import { IntlMessageFormat } from 'intl-messageformat'
import en from '@design/i18n/en.json'
import { FLAGS, type Flags } from './flags'

export type Locale = 'en' | 'fil' | 'ceb' | 'es'

/**
 * The languages a guest can pick, with each one's name IN ITS OWN LANGUAGE: a guest who reads only Bisaya must be able to find "Bisaya"
 * without reading English. No flags, and never the browser's language: a phone set to English belongs to people who read Bisaya.
 */
export const GUEST_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'fil', label: 'Tagalog' },
  { code: 'ceb', label: 'Bisaya' },
] as const satisfies readonly { code: Locale; label: string }[]

export function isGuestLocale(code: string | null | undefined): code is 'en' | 'fil' | 'ceb' {
  return GUEST_LOCALES.some((l) => l.code === code)
}
export type Messages = Record<string, unknown>

// English is bundled; Spanish is loaded on demand. Per CLAUDE.md the launch is
// English-only and es.json stays behind a feature flag — so it must not weigh
// on the guest page's 150 KB.
const bundled: Messages = en as Messages

let active: Messages = bundled
let activeLocale: Locale = 'en'

// The polite set (Tom, 30 Oct 2026): at a lamay and a christening, older relatives are the guests, and Tagalog speaks to them with "po" and "ninyo".
// A polite text exists only for the strings that speak to the guest; every other key falls back to the normal text. English has no polite set.
// The file is fetched only for such an event, in Tagalog or Bisaya (docs/design/i18n/polite/; reviewed like the rest, docs/translations.md).
let politeOn = false
let politeMessages: Messages = {}

/** A lamay (the memorial look) or a christening: the guests are spoken to politely. */
export function isPoliteEvent(e: { type?: string | null; memorial?: boolean | null; group?: string | null } | null | undefined): boolean {
  return e?.type === 'christening' || e?.memorial === true || e?.group === 'memorial'
}

async function politeFor(l: Locale): Promise<Messages> {
  if (l === 'fil') return (await import('@design/i18n/polite/fil.json')).default as Messages
  if (l === 'ceb') return (await import('@design/i18n/polite/ceb.json')).default as Messages
  return {}
}

/** Switches the polite set on or off for the page's own language. */
export async function setPolite(on: boolean): Promise<void> {
  politeOn = on
  politeMessages = on ? await politeFor(activeLocale) : {}
}

export function polite(): boolean {
  return politeOn
}

export function locale(): Locale {
  return activeLocale
}

export async function setLocale(next: Locale, flags: Flags = FLAGS): Promise<void> {
  // Spanish is refused while its flag is off, rather than quietly working: a
  // language switcher added later would otherwise ship untranslated-by-a-human
  // legal text the moment someone wires it up. Stays on the current locale.
  if (next === 'es' && !flags.spanish) return
  if (next === 'en') {
    active = bundled
    activeLocale = 'en'
  } else {
    // One small file, fetched only for the guest who needs it. Strings it does not have yet fall back to English (see t()).
    const mod = next === 'fil' ? await import('@design/i18n/fil.json') : next === 'ceb' ? await import('@design/i18n/ceb.json') : await import('@design/i18n/es.json')
    active = mod.default as Messages
    activeLocale = next
  }
  politeMessages = politeOn ? await politeFor(activeLocale) : {}
  if (typeof document !== 'undefined') document.documentElement.lang = activeLocale
}

function lookup(messages: Messages, key: string): string | undefined {
  // Keys are dotted paths ("welcome.title"); the files are nested.
  let node: unknown = messages
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined
    node = (node as Record<string, unknown>)[part]
  }
  return typeof node === 'string' ? node : undefined
}

const cache = new Map<string, IntlMessageFormat>()

/**
 * Translate `key`, filling in `values`.
 *
 * A missing key returns the key itself rather than throwing or returning empty.
 * An empty string on a screen is a bug nobody notices; a visible "welcome.title"
 * is a bug someone reports. The i18n test keeps this from happening in the
 * first place.
 */
export function t(key: string, values?: Record<string, unknown>): string {
  const politeRaw = politeOn ? lookup(politeMessages, key) : undefined
  const raw = politeRaw ?? lookup(active, key) ?? lookup(bundled, key)
  if (raw === undefined) return key

  const cacheKey = `${activeLocale}:${politeRaw === undefined ? '' : 'polite:'}${key}`
  let fmt = cache.get(cacheKey)
  if (!fmt) {
    try {
      fmt = new IntlMessageFormat(raw, activeLocale)
    } catch {
      fmt = new IntlMessageFormat(raw, 'en') // a browser that does not know this locale's plural rules still gets a sentence
    }
    cache.set(cacheKey, fmt)
  }
  const out = fmt.format(values ?? {})
  return typeof out === 'string' ? out : String(out)
}

/**
 * A function that translates into one language WITHOUT changing the page's own: a card is printed in the event's language while the dashboard stays in
 * English. Strings a language does not have fall back to English, like `t`.
 */
export async function translatorFor(next: 'en' | 'fil' | 'ceb', options: { polite?: boolean } = {}): Promise<(key: string, values?: Record<string, unknown>) => string> {
  const messages: Messages =
    next === 'en' ? bundled : ((next === 'fil' ? await import('@design/i18n/fil.json') : await import('@design/i18n/ceb.json')).default as Messages)
  const politeSet = options.polite ? await politeFor(next) : {}
  return (key, values) => {
    const raw = lookup(politeSet, key) ?? lookup(messages, key) ?? lookup(bundled, key)
    if (raw === undefined) return key
    let fmt: IntlMessageFormat
    try {
      fmt = new IntlMessageFormat(raw, next)
    } catch {
      fmt = new IntlMessageFormat(raw, 'en')
    }
    const out = fmt.format(values ?? {})
    return typeof out === 'string' ? out : String(out)
  }
}

/** Every key in a message file, as dotted paths. Used by the i18n test. */
export function flatten(messages: Messages, prefix = ''): Map<string, string> {
  const out = new Map<string, string>()
  for (const [k, v] of Object.entries(messages)) {
    const path = prefix ? `${prefix}.${k}` : k
    if (typeof v === 'string') out.set(path, v)
    else if (typeof v === 'object' && v !== null) {
      for (const [ik, iv] of flatten(v as Messages, path)) out.set(ik, iv)
    }
  }
  return out
}
